import dayjs from "dayjs";
import { slotTimeRanges } from "@/constants";
import { DatePattern, formatIsoDate } from "@/utils/date-format";
import { slotLabel } from "./hall-calendar-format";
import type { HallBookingCalendarEvent, HallBookingResource } from "./types";

type HallRef = {
  id?: string;
  name?: string;
  shortCode?: string;
  color?: string | null;
};

type BookingRef = {
  paidAmount?: number;
  depositPaidAmount?: number;
  organiser?: string;
  itsNo?: string;
  phone?: string;
};

function slotHours(slot: unknown): [number, number] {
  return slotTimeRanges[String(slot ?? "")] ?? [0, 1];
}

function eventTimes(date: unknown, slot: unknown): { start: Date; end: Date } | null {
  const ymd = formatIsoDate(date as string, { utc: true });
  if (!ymd) return null;
  const day = dayjs(ymd, DatePattern.ISO_DATE, true);
  if (!day.isValid()) return null;
  const [startHour, endHour] = slotHours(slot);
  const start = day.hour(startHour).minute(0).second(0).toDate();
  const end = day.hour(endHour).minute(0).second(0).toDate();
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return { start, end };
}

export function mapHallBookingRowToCalendarEvent(
  book: Record<string, unknown>
): HallBookingCalendarEvent | null {
  const times = eventTimes(book.date, book.slot);
  if (!times) return null;

  const hall = book.hall as HallRef | undefined;
  const title = `${hall?.name || "N/A"}`;
  const subTitle = `${slotLabel(String(book.slot ?? ""))}`;

  if (book.isBlockedDate) {
    return {
      id: book.id as string | number,
      title,
      subTitle,
      start: times.start,
      end: times.end,
      isBlockedDate: true,
      purpose: book.purpose as string | undefined,
      resource: {
        hall,
        purpose: book.purpose as string | undefined,
        isBlockedDate: true,
      },
    };
  }

  const booking = book.booking as BookingRef | undefined;
  return {
    id: book.id as string | number,
    title,
    subTitle,
    start: times.start,
    end: times.end,
    tentative: !((booking?.paidAmount ?? 0) + (booking?.depositPaidAmount ?? 0) > 0),
    resource: book as unknown as HallBookingResource,
  };
}

export function mapHallBookingRowsToCalendarEvents(rows: unknown[]): HallBookingCalendarEvent[] {
  return (rows as Record<string, unknown>[])
    .map((row) => mapHallBookingRowToCalendarEvent(row))
    .filter((event): event is HallBookingCalendarEvent => event != null);
}
